// Vue « Semaine » de l'écran « Tes actions » : récapitulatif LECTURE SEULE
// agrégé sur toute la fenêtre du challenge (aucune validation possible ici).
// Données : historique daily_actions via useChallengeHistory() (bornes réelles,
// mode test géré par toRealDate() dans le hook).

import {
  ACTIONS,
  CATEGORY_LABEL_KEYS,
  CATEGORY_ORDER,
  LOTO_BONUS_POINTS,
  getActionsByCategory,
} from '@/data/actions';
import { useChallengeHistory } from '@/hooks/useDailyActions';
import { computeLotoBonus } from '@/utils/scoring';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

export default function WeeklyRecap() {
  const { t } = useTranslation();
  const router = useRouter();
  const { history, loading } = useChallengeHistory();

  // Nombre de jours où chaque action a été validée (1 ligne daily_actions
  // par action et par jour → un simple comptage suffit).
  const countsByAction = useMemo(() => {
    const counts = new Map<number, number>();
    for (const entry of history) {
      counts.set(entry.actionId, (counts.get(entry.actionId) ?? 0) + 1);
    }
    return counts;
  }, [history]);

  // Lignes complètes : pour chaque jour, une catégorie dont les 3 actions ont
  // été validées le même jour = +50 pts (même règle que computeLotoBonus à la
  // validation : on lui passe les actions du jour comme « pending »).
  const completedLines = useMemo(() => {
    const idsByDay = new Map<string, number[]>();
    for (const entry of history) {
      const ids = idsByDay.get(entry.actionDate) ?? [];
      ids.push(entry.actionId);
      idsByDay.set(entry.actionDate, ids);
    }
    let lines = 0;
    for (const ids of idsByDay.values()) {
      lines += computeLotoBonus(ACTIONS, [], ids) / LOTO_BONUS_POINTS;
    }
    return lines;
  }, [history]);

  const groupedActions = useMemo(() => getActionsByCategory(), []);

  const actionPointsTotal = useMemo(() => {
    let total = 0;
    for (const action of ACTIONS) {
      total += (countsByAction.get(action.id) ?? 0) * action.points;
    }
    return total;
  }, [countsByAction]);

  const bonusPointsTotal = completedLines * LOTO_BONUS_POINTS;
  const grandTotal = actionPointsTotal + bonusPointsTotal;

  const goToProfile = () => router.push('/(tabs)/profil');

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#1C453F" />
      </View>
    );
  }

  if (history.length === 0) {
    return (
      <View style={styles.centered}>
        <Ionicons name="leaf-outline" size={40} color="#78A584" />
        <Text style={styles.emptyTitle}>{t('home.weeklyRecap.emptyTitle')}</Text>
        <Text style={styles.emptySubtitle}>
          {t('home.weeklyRecap.emptySubtitle')}
        </Text>
        <TouchableOpacity
          style={styles.profileButton}
          onPress={goToProfile}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel={t('home.weeklyRecap.seeProfileButton')}
        >
          <Text style={styles.profileButtonText}>{t('home.weeklyRecap.seeProfileButton')}</Text>
          <Ionicons name="arrow-forward" size={16} color="#1C453F" />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View>
      {CATEGORY_ORDER.map((category) => {
        const actions = (groupedActions[category] ?? []).filter(
          (action) => (countsByAction.get(action.id) ?? 0) > 0
        );
        if (actions.length === 0) return null;
        return (
          <View key={category} style={styles.categorySection}>
            <Text style={styles.categoryTitle}>{t(CATEGORY_LABEL_KEYS[category])}</Text>
            <View style={styles.card}>
              {actions.map((action, index) => {
                const count = countsByAction.get(action.id) ?? 0;
                return (
                  <View
                    key={action.id}
                    style={[styles.row, index > 0 && styles.rowBorder]}
                  >
                    <Text style={styles.rowLabel} numberOfLines={1}>
                      {t('home.weeklyRecap.actionRow', { count, name: t(action.nameKey) })}
                    </Text>
                    <Text style={styles.rowPoints}>
                      {t('common.pointsShort', { points: count * action.points })}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        );
      })}

      {completedLines > 0 && (
        <View style={styles.categorySection}>
          <Text style={styles.categoryTitle}>{t('home.weeklyRecap.bonusTitle')}</Text>
          <View style={styles.card}>
            <View style={styles.row}>
              <View style={styles.bonusLabelRow}>
                <Ionicons name="trophy" size={14} color="#83BF4F" />
                <Text style={styles.rowLabel}>
                  {t('home.weeklyRecap.completedLines', {
                    count: completedLines,
                    points: LOTO_BONUS_POINTS,
                  })}
                </Text>
              </View>
              <Text style={styles.rowPointsBonus}>
                {t('common.pointsShort', { points: bonusPointsTotal })}
              </Text>
            </View>
          </View>
        </View>
      )}

      <View style={styles.totalCard}>
        <Text style={styles.totalLabel}>{t('home.weeklyRecap.totalLabel')}</Text>
        <Text style={styles.totalPoints}>
          {t('common.pointsShort', { points: grandTotal })}
        </Text>
      </View>

      <TouchableOpacity
        style={styles.profileButton}
        onPress={goToProfile}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel={t('home.weeklyRecap.seeProfileButton')}
      >
        <Text style={styles.profileButtonText}>{t('home.weeklyRecap.seeProfileButton')}</Text>
        <Ionicons name="arrow-forward" size={16} color="#1C453F" />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  centered: {
    paddingVertical: 48,
    alignItems: 'center',
    gap: 12,
  },
  emptyTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: '#000',
    textAlign: 'center',
  },
  emptySubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    lineHeight: 19,
    paddingHorizontal: 12,
  },
  categorySection: {
    marginBottom: 16,
  },
  categoryTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: '#000',
    marginBottom: 8,
  },
  card: {
    backgroundColor: '#FFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    gap: 8,
  },
  rowBorder: {
    borderTopWidth: 1,
    borderTopColor: '#F0E7DB',
  },
  rowLabel: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#000',
    flexShrink: 1,
  },
  rowPoints: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: '#CD7728',
  },
  bonusLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
  },
  rowPointsBonus: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: '#83BF4F',
  },
  totalCard: {
    backgroundColor: '#1C453F',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  totalLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 15,
    color: '#FFF',
  },
  totalPoints: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: '#FCC189',
  },
  profileButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: '#1C453F',
    backgroundColor: 'transparent',
    alignSelf: 'stretch',
  },
  profileButtonText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 15,
    color: '#1C453F',
  },
});
